import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../app/hooks";
import { fetchMemberNotificationPreferences } from "../../features/members/slice-extensions/members-slice-extension";
import { MemberNotificationPreferencesResponse } from "../../features/members/contracts/MemberNotificationPreferencesResponse";
import { AnnouncementOptOutSource } from "../../features/members/enums/AnnouncementOptOutSource";
import { getDisplayLocale } from "../../features/utils/timezoneUtils";
import { formatMediumDate } from "../../features/utils/displayFormat";

interface Props {
  memberId: string;
}

/**
 * What this member has chosen to receive — read-only on purpose. Only the member
 * can change it (portal setting or the unsubscribe link in an email), so an
 * instructor can see a withdrawal but never undo it.
 */
const MemberEmailPreferencesRow: React.FC<Props> = ({ memberId }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [preferences, setPreferences] = useState<MemberNotificationPreferencesResponse | null>(
    null
  );

  useEffect(() => {
    let active = true;
    dispatch(fetchMemberNotificationPreferences(memberId))
      .unwrap()
      .then((result) => {
        if (active) setPreferences(result);
      })
      .catch(() => {
        if (active) setPreferences(null);
      });

    return () => {
      active = false;
    };
  }, [memberId, dispatch]);

  if (!preferences) {
    return null;
  }

  // The same medium date as the consents above it, so the card reads one date format.
  const formatDate = (value: string) => formatMediumDate(value, getDisplayLocale());
  const sourceLabel =
    preferences.optedOutVia === AnnouncementOptOutSource.UnsubscribeLink
      ? t("members.emailPreferences.viaUnsubscribeLink", "via unsubscribe link")
      : t("members.emailPreferences.viaPortal", "in the member portal");

  return (
    <div className="mt-4 pt-4 border-t border-grey-70 dark:border-primary-20 space-y-3">
      <PreferenceLine
        title={t("members.emailPreferences.title", "Announcement emails")}
        enabled={preferences.announcementsEnabled}
        description={
          preferences.announcementsEnabled
            ? t(
                "members.emailPreferences.receiving",
                "Receiving announcements. Only the member can change this."
              )
            : t("members.emailPreferences.unsubscribed", {
                defaultValue: "Unsubscribed {{date}} {{source}}.",
                date: preferences.optedOutAt ? formatDate(preferences.optedOutAt) : "",
                source: sourceLabel,
              })
        }
      />
      <PreferenceLine
        title={t("members.emailPreferences.sessionReminders", "{{Session}} reminders")}
        enabled={preferences.sessionReminders}
        description={
          preferences.sessionReminders
            ? t(
                "members.emailPreferences.sessionRemindersReceiving",
                "Receiving reminder emails. Only the member can change this."
              )
            : t("members.emailPreferences.sessionRemindersOff", "Switched off by the member.")
        }
      />
    </div>
  );
};

interface PreferenceLineProps {
  title: string;
  description: string;
  enabled: boolean;
}

/** One read-only switch state: what it is, what it means, and On / Off. */
const PreferenceLine: React.FC<PreferenceLineProps> = ({ title, description, enabled }) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className="text-grey-20 dark:text-white text-sm font-medium">{title}</p>
        <p className="text-grey-30 dark:text-grey-70 text-xs mt-0.5">{description}</p>
      </div>
      <span
        className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
          enabled
            ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
            : "bg-grey-95 text-grey-30 dark:bg-primary-20 dark:text-grey-70"
        }`}
      >
        {enabled ? t("members.emailPreferences.on", "On") : t("members.emailPreferences.off", "Off")}
      </span>
    </div>
  );
};

export default MemberEmailPreferencesRow;
